import { DatePicker } from "@/components/ui/date-picker";
import {
  dateToWallDate,
  formatCzDate,
  isoToPragueDate,
  pragueWallToIso,
  todayPragueDate,
} from "@/lib/datetime";

/**
 * "Suggest from" — the date a newly connected source (a WhatsApp chat, a
 * Discord channel) starts producing suggestions. Shared by both connect
 * dialogs so the question, its default and its wording are the same wherever
 * a source is hooked up to the assistant.
 *
 * Hub reads a further month *before* that date as context only: the model may
 * use it to understand what the analysed messages refer to, but never proposes
 * anything from it. So the date is where the queue starts, not where the
 * reading starts.
 *
 * The value is a Prague wall date (`YYYY-MM-DD`); an empty string means "only
 * what arrives from now on".
 */

/** Sensible starting point: a month back — enough context to be useful,
 *  short enough not to re-open long-settled conversations. */
export function defaultHistoryFrom(): string {
  return dateToWallDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
}

/** The wall date as the absolute instant the API expects, or null for
 *  "no history". */
export function historyFromPayload(wallDate: string): string | null {
  return wallDate ? pragueWallToIso(`${wallDate}T00:00`) : null;
}

/** An ISO instant from the API rendered as `dd.mm.yyyy`. */
export function formatHistoryFrom(iso: string): string {
  return formatCzDate(isoToPragueDate(iso));
}

/** The per-row note: where suggestions start, and how much older history the
 *  model has as context. */
export function historyNote(historyFrom: string | null, contextCount: number): string {
  const context = contextCount > 0 ? ` (+${contextCount} for context)` : "";
  return historyFrom
    ? `suggests from ${formatHistoryFrom(historyFrom)}${context}`
    : `no history loaded${context}`;
}

export function HistoryFromField({
  value,
  onChange,
}: {
  value: string;
  onChange: (wallDate: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor="history-from" className="text-sm font-medium leading-none">
        Suggest from
      </label>
      <DatePicker
        id="history-from"
        value={value}
        onChange={onChange}
        max={todayPragueDate()}
        aria-label="Suggest from"
      />
      <p className="text-xs text-muted-foreground">
        Suggestions come only from messages sent on or after this date. Hub also loads the month
        before it, but only as context the AI reads to understand them — nothing older is ever
        proposed. Leave it empty to start from now.
      </p>
    </div>
  );
}
